import { useMemo } from 'react';
import { useQueries, useQuery } from 'react-query';
import useAuth from '@/hooks/useAuth';
import { getPendingInvitations, getTeamMembers } from '@/services/business';
import { getCardLimits, getCardRules, getCards } from '@/services/card';
import { CardRuleTypeEnum, CardRuleActionEnum } from '@/services/types/Card';
import {
  buildPendingInviteRows,
  buildTeamMemberRows,
  pickPrimaryCardLimit,
  TCardFrozenMap,
  TCardLimitMap
} from '../utils/buildTeamMemberRows';

export const TEAM_MEMBERS_QUERY_KEY = 'teamMembers';
export const TEAM_MEMBER_CARDS_QUERY_KEY = 'teamMemberCards';
export const TEAM_MEMBER_PENDING_INVITATIONS_QUERY_KEY = 'teamMemberPendingInvitations';
// Singular and per-card (see cardRuleQueries below) - was previously one aggregate query keyed
// by the whole card id list, which meant ANY change anywhere (one card frozen, one new card
// created) re-fetched every other card's rules too, all over again.
export const TEAM_MEMBER_CARD_RULE_QUERY_KEY = 'teamMemberCardRule';
// Same per-card, independently-cached shape as TEAM_MEMBER_CARD_RULE_QUERY_KEY, for the list
// row's "Limit $X.XX" badge (see CardSettingsModal, the only place these limits are edited).
export const TEAM_MEMBER_CARD_LIMIT_QUERY_KEY = 'teamMemberCardLimit';

/** What a mutation should refetch afterwards - deliberately granular (see
 *  useTeamMemberSelectionActions/useCreateTeamMemberCard) so e.g. updating one member's access
 *  level doesn't also re-fetch every card's freeze state, and freezing two cards doesn't
 *  re-fetch rules for every *other* card in the business. */
export type TTeamMembersRefetch = {
  members: () => void;
  cards: () => void;
  /** Omit cardIds to refetch every card's rules; pass specific ids to refetch only those. */
  cardRules: (cardIds?: string[]) => void;
  /** Omit cardIds to refetch every card's limits; pass specific ids to refetch only those. */
  cardLimits: (cardIds?: string[]) => void;
  invitations: () => void;
};

/**
 * Fetches the full member list and the full card list for the business once, and caches both
 * (see queryClient.ts) - deliberately no server-side pagination/search: bulk row actions (select
 * all, then act) need the whole dataset in hand at once, so search/sort/selection all happen
 * client-side over this cached list instead of round-tripping per keystroke or per page.
 */
export const useTeamMembers = () => {
  const auth = useAuth();
  const businessId = auth?.business?.id;

  const membersQuery = useQuery(
    [TEAM_MEMBERS_QUERY_KEY, businessId],
    () => getTeamMembers(businessId as string).then((response) => response.data),
    { enabled: Boolean(businessId) }
  );

  const cardsQuery = useQuery(
    [TEAM_MEMBER_CARDS_QUERY_KEY, businessId],
    () =>
      Promise.all([
        getCards({ businessId, ownedType: 'personal' }),
        getCards({ businessId, ownedType: 'team' })
      ]).then(([personal, team]) => [...personal.data, ...team.data]),
    { enabled: Boolean(businessId) }
  );

  const invitationsQuery = useQuery(
    [TEAM_MEMBER_PENDING_INVITATIONS_QUERY_KEY, businessId],
    () => getPendingInvitations(businessId as string).then((response) => response.data),
    { enabled: Boolean(businessId) }
  );

  const cardIds = useMemo(() => (cardsQuery.data ?? []).map((card) => card.id), [cardsQuery.data]);

  // GET /cards doesn't expose a frozen flag - a card is only frozen via an "all" type rule, only
  // visible per-card via GET /cards/{cardId}/rules. There's no bulk/business-scoped variant of
  // that endpoint, so this fans out one request per card - but as genuinely independent cached
  // queries (one per cardId), not one aggregate query re-run over the whole list: a new card
  // added to cardIds only fetches its own (new) query, every other card's already-cached result
  // is left alone.
  const cardRuleQueries = useQueries(
    cardIds.map((cardId) => ({
      queryKey: [TEAM_MEMBER_CARD_RULE_QUERY_KEY, cardId],
      queryFn: () => getCardRules(cardId).then((response) => response.data)
    }))
  );

  const frozenByCardId = useMemo(() => {
    const map: TCardFrozenMap = {};
    cardIds.forEach((cardId, index) => {
      const rules = cardRuleQueries[index]?.data;
      if (rules) {
        map[cardId] = rules.some(
          (rule) => rule.type === CardRuleTypeEnum.ALL && rule.action === CardRuleActionEnum.BLOCK
        );
      }
    });
    return map;
    // cardRuleQueries is a fresh array every render - depending on each query's own .data value
    // (not the array itself) is what actually makes this recompute only when a rule changes.
  }, [cardIds, cardRuleQueries]);

  // Same fan-out shape as cardRuleQueries (see its own comment) - GET /cards likewise exposes no
  // limit info, and GET .../limits is per-card with no bulk/business-scoped variant.
  const cardLimitQueries = useQueries(
    cardIds.map((cardId) => ({
      queryKey: [TEAM_MEMBER_CARD_LIMIT_QUERY_KEY, cardId],
      queryFn: () => getCardLimits(cardId).then((response) => response.data)
    }))
  );

  const cardLimitByCardId = useMemo(() => {
    const map: TCardLimitMap = {};
    cardIds.forEach((cardId, index) => {
      map[cardId] = pickPrimaryCardLimit(cardLimitQueries[index]?.data);
    });
    return map;
  }, [cardIds, ...cardLimitQueries.map((query) => query.data)]);

  const rows = useMemo(
    () => [
      ...buildPendingInviteRows(invitationsQuery.data ?? []),
      ...buildTeamMemberRows(membersQuery.data ?? [], cardsQuery.data ?? [], frozenByCardId)
    ],
    [invitationsQuery.data, membersQuery.data, cardsQuery.data, frozenByCardId]
  );

  const refetchCardRules = (targetCardIds?: string[]) => {
    cardIds.forEach((cardId, index) => {
      if (!targetCardIds || targetCardIds.includes(cardId)) {
        cardRuleQueries[index]?.refetch();
      }
    });
  };

  const refetchCardLimits = (targetCardIds?: string[]) => {
    cardIds.forEach((cardId, index) => {
      if (!targetCardIds || targetCardIds.includes(cardId)) {
        cardLimitQueries[index]?.refetch();
      }
    });
  };

  const refetch: TTeamMembersRefetch = {
    members: () => membersQuery.refetch(),
    cards: () => cardsQuery.refetch(),
    cardRules: refetchCardRules,
    cardLimits: refetchCardLimits,
    invitations: () => invitationsQuery.refetch()
  };

  return {
    rows,
    cards: cardsQuery.data ?? [],
    // Raw invitation records (with firstName/lastName as separate fields, unlike TTeamMemberRow's
    // combined displayName) - InviteTeamMemberModal needs these directly to edit an existing
    // invite, rather than re-deriving them by splitting a rendered display string.
    pendingInvitations: invitationsQuery.data ?? [],
    frozenByCardId,
    cardLimitByCardId,
    businessId,
    isLoading: membersQuery.isLoading || cardsQuery.isLoading || invitationsQuery.isLoading,
    isError: membersQuery.isError || cardsQuery.isError || invitationsQuery.isError,
    refetch
  };
};
